// Story 5.10 — "Make an offer" dialog (buyer-facing).
//
// Vault-ledger sibling of ListingDialog. Where the list-for-sale dialog is
// seller-facing (asking price + fee split), this is buyer-facing: an offer
// BELOW the asking price whose funds are HELD (moved to escrow) the moment
// it's placed and released on withdraw / reject / auto-reject. So the panel
// shows the buyer's balance impact + the hold explainer, not the 10/5/85
// split (that's the seller's concern, surfaced at accept time).
//
// Outcome handling:
//   * success → toast + onPlaced() [invalidation] then close
//   * 402 INSUFFICIENT_BALANCE → in-dialog alert + Top-up link; stays open
//   * 409 OFFER_ALREADY_EXISTS / BIDDING_NOT_ALLOWED / LISTING_NOT_ACTIVE →
//     in-dialog message; stays open
//   * 400/500 → in-dialog error + toast; stays open

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
} from "@greenhold/components";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { eurosStringToCents, formatCents } from "./feeMath";
import { usePlaceOffer, type OfferError, type OfferKind } from "./useMarketplaceOffers";

export interface MakeOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: OfferKind;
  listingId: string;
  /** e.g. "certificate" | "sealed dossier" — fills the title. */
  itemNoun: string;
  askingPriceCents: number;
  /** Minimum offer in cents (share €0.20 = 20; dossier €1.00 = 100). */
  minCents: number;
  /** Current wallet balance in cents; null while loading. */
  balanceCents: number | null;
  /** Success — do the query invalidation. The dialog then closes. */
  onPlaced: () => void;
}

export function MakeOfferDialog({
  open,
  onOpenChange,
  kind,
  listingId,
  itemNoun,
  askingPriceCents,
  minCents,
  balanceCents,
  onPlaced,
}: MakeOfferDialogProps) {
  const [amountEuros, setAmountEuros] = useState("");
  const [insufficient, setInsufficient] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);

  const placeOffer = usePlaceOffer(kind);

  const idPrefix = kind === "share" ? "offer" : "offer-sd";
  const amountInputId = `${idPrefix}-amount-input`;
  const amountHelpId = `${idPrefix}-amount-help`;

  useEffect(() => {
    if (!open) {
      setAmountEuros("");
      setInsufficient(false);
      setGenericError(null);
    }
  }, [open]);

  const amountCents = eurosStringToCents(amountEuros);
  const amountFinite = Number.isFinite(amountCents);
  const belowFloor = amountFinite && amountCents < minCents;
  const atOrAboveAsking = amountFinite && amountCents >= askingPriceCents;
  const exceedsBalance =
    amountFinite && balanceCents !== null && amountCents > balanceCents;
  const amountValid =
    amountFinite && !belowFloor && !atOrAboveAsking && !exceedsBalance;

  const balanceAfter =
    amountValid && balanceCents !== null ? balanceCents - amountCents : null;

  const submitDisabled = !amountValid || placeOffer.isPending;

  async function handleSubmit() {
    setInsufficient(false);
    setGenericError(null);
    try {
      await placeOffer.mutateAsync({
        listing_id: listingId,
        amount_cents: amountCents,
      });
      toast.success("Offer placed — funds are held until the seller responds.");
      onPlaced();
      onOpenChange(false);
    } catch (err) {
      const detail = err as OfferError;
      switch (detail?.code) {
        case "INSUFFICIENT_BALANCE":
        case "WALLET_INSUFFICIENT_BALANCE":
          setInsufficient(true);
          return;
        case "OFFER_ALREADY_EXISTS":
          setGenericError(
            "You already have an active offer on this listing. Withdraw it first to place a new one.",
          );
          return;
        case "BIDDING_NOT_ALLOWED":
          setGenericError("This listing no longer accepts offers.");
          return;
        case "OFFER_BLOCKED":
          setGenericError("You can't make another offer on this listing.");
          return;
        case "LISTING_NOT_ACTIVE":
        case "LISTING_NOT_FOUND":
          setGenericError("This listing is no longer available.");
          return;
        default:
          setGenericError(detail?.message ?? "Could not place your offer. Try again.");
          toast.error(detail?.message ?? "Could not place your offer. Try again.");
      }
    }
  }

  const helpText = belowFloor
    ? `Minimum offer is ${formatCents(minCents)}.`
    : atOrAboveAsking
      ? `Your offer must be below the asking price of ${formatCents(askingPriceCents)}. To pay full price, use Buy instead.`
      : exceedsBalance
        ? "That's more than your available balance."
        : `Asking price is ${formatCents(askingPriceCents)}. Offer anything below it.`;

  const helpTone =
    belowFloor || atOrAboveAsking || exceedsBalance
      ? "text-state-error-fill"
      : "text-brand-forest-700";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[85vh] max-w-md gap-0 overflow-y-auto overflow-x-hidden border border-brand-gold-500/40 bg-brand-parchment p-0 text-brand-ink-900 shadow-xl sm:rounded-xl">
        <AlertDialogHeader className="space-y-1.5 border-b border-brand-gold-500/40 bg-brand-parchment-hi px-6 pb-5 pt-6 text-left">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-forest-700">
            Greenhold · Marketplace
          </p>
          <AlertDialogTitle className="font-heading text-2xl font-semibold leading-tight tracking-tight text-brand-ink-900">
            Make an offer
          </AlertDialogTitle>
          <AlertDialogDescription className="font-body text-sm text-brand-ink-700">
            Offer below the asking price on this {itemNoun}. Your funds are held
            the moment you offer, and released instantly if you withdraw or the
            seller declines.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-5 px-6 pb-6 pt-6">
          {/* Offer amount */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Label
                htmlFor={amountInputId}
                className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700"
              >
                Your offer
              </Label>
              <span
                className="h-px flex-1 bg-brand-gold-500/40"
                aria-hidden="true"
              />
            </div>
            <div className="relative">
              <span
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 font-mono text-lg font-semibold text-brand-forest-700"
                aria-hidden="true"
              >
                €
              </span>
              <Input
                id={amountInputId}
                type="number"
                inputMode="decimal"
                min={minCents / 100}
                max={(askingPriceCents - 1) / 100}
                step={0.01}
                value={amountEuros}
                onChange={(e) => setAmountEuros(e.target.value)}
                placeholder={(minCents / 100).toFixed(2)}
                disabled={placeOffer.isPending}
                aria-describedby={amountHelpId}
                className="h-12 rounded-lg border-brand-gold-500/50 bg-brand-paper-50 pl-9 pr-4 font-mono text-lg font-semibold tabular-nums text-brand-ink-900 placeholder:font-normal placeholder:text-brand-ink-700/70 focus-visible:border-brand-forest-500 focus-visible:ring-brand-forest-500/30"
              />
            </div>
            <p
              id={amountHelpId}
              className={`font-mono text-[11px] tracking-wide ${helpTone}`}
            >
              {helpText}
            </p>
          </div>

          {/* Hold summary — buyer-facing balance impact */}
          <div className="rounded-lg border border-brand-gold-500/40 bg-brand-parchment-hi p-4">
            <div className="mb-3 flex items-center gap-3">
              <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700">
                Funds held
              </span>
              <span
                className="h-px flex-1 bg-brand-gold-500/40"
                aria-hidden="true"
              />
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-brand-ink-700">You hold</dt>
                <dd
                  data-testid="offer-hold-amount"
                  className="font-mono tabular-nums text-brand-ink-900"
                >
                  {amountValid ? formatCents(amountCents) : "—"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-brand-ink-700">Current balance</dt>
                <dd className="font-mono tabular-nums text-brand-ink-900">
                  {balanceCents !== null ? formatCents(balanceCents) : "—"}
                </dd>
              </div>
              <div className="mt-1 flex items-baseline justify-between border-t border-brand-gold-500/40 pt-2">
                <dt className="font-semibold text-brand-ink-900">
                  Balance after hold
                </dt>
                <dd
                  data-testid="offer-balance-after"
                  className="font-mono text-base font-bold tabular-nums text-brand-forest-700"
                >
                  {balanceAfter !== null ? formatCents(balanceAfter) : "—"}
                </dd>
              </div>
            </dl>
            <p className="mt-3 font-body text-xs text-brand-ink-700">
              Held funds stay reserved until the seller accepts or declines, or
              you withdraw the offer — you can free them anytime from{" "}
              <span className="font-semibold">My offers</span>.
            </p>
          </div>

          {insufficient && (
            <div
              role="status"
              data-testid="offer-insufficient-balance"
              className="space-y-2 rounded-lg border border-state-warning-fill/50 bg-brand-paper-50 p-3"
            >
              <p className="font-body text-sm text-brand-ink-900">
                Your balance is too low to hold this offer.
              </p>
              <Link
                to="/account/wallet"
                className="inline-block rounded-lg bg-state-warning-fill px-4 py-2 text-center text-sm font-medium text-state-warning-fg hover:brightness-110"
              >
                Top up your wallet
              </Link>
            </div>
          )}
          {genericError && (
            <p
              role="alert"
              className="flex items-center gap-2 rounded-md border border-state-error-fill/30 bg-brand-paper-50 px-3 py-2 font-mono text-xs font-semibold text-state-error-fill"
            >
              <span aria-hidden="true">⚠</span>
              {genericError}
            </p>
          )}
        </div>

        <AlertDialogFooter className="gap-2 border-t border-brand-gold-500/40 bg-brand-parchment-hi px-6 py-4">
          <AlertDialogCancel
            disabled={placeOffer.isPending}
            className="rounded-lg font-mono text-xs font-semibold uppercase tracking-wide"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={submitDisabled}
            onClick={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            className="rounded-lg font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-md hover:shadow-lg"
          >
            {placeOffer.isPending
              ? "Placing…"
              : amountValid
                ? `Place offer — ${formatCents(amountCents)}`
                : "Place offer"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
